<template>
  <div class="period-limit-matrix">
    <div
      v-for="group in yearGroups"
      :key="group.year"
      class="period-limit-matrix__year"
      :class="{ 'is-invalid': yearIssueCount(group) > 0 }"
    >
      <div class="period-limit-matrix__header">
        <h6 class="period-limit-matrix__year-title">{{ group.year }}</h6>
        <span
          v-if="yearPartialRangeLabel(group)"
          class="period-limit-matrix__partial-range"
        >
          {{ yearPartialRangeLabel(group) }}
        </span>
        <span
          v-if="hasPartialCells(group)"
          class="period-limit-matrix__badge is-warning"
        >
          {{ $t("portfolio_instructions.partial_period_badge") }}
        </span>
        <span
          v-if="group.isLeapYear"
          class="period-limit-matrix__badge is-info"
        >
          {{ $t("portfolio_instructions.leap_year_badge") }}
        </span>
        <span
          v-if="yearIssueCount(group) > 0"
          class="period-limit-matrix__badge is-danger"
        >
          <FontAwesomeIcon icon="circle-exclamation" />
          {{
            $t("portfolio_instructions.year_issues_badge", {
              count: yearIssueCount(group),
            })
          }}
        </span>
        <div class="period-limit-matrix__actions">
          <button
            v-if="yearGroups.length > 1"
            type="button"
            class="btn period-limit-matrix__apply"
            :disabled="!hasFullCells(group)"
            :title="$t('portfolio_instructions.apply_to_all_years_tooltip')"
            @click="applyYearToAllYears(group.year)"
          >
            {{ $t("portfolio_instructions.apply_to_all_years") }}
          </button>
          <button
            type="button"
            class="btn period-limit-matrix__reset"
            @click="resetYear(group.year)"
          >
            {{ $t("portfolio_instructions.reset_year") }}
          </button>
        </div>
      </div>

      <div class="period-limit-matrix__grid" :class="{ 'is-yearly': isYearly }">
        <div
          v-for="cell in group.cells"
          :key="`${group.year}-${cell.startDate}`"
          class="period-limit-matrix__cell"
        >
          <label class="period-limit-matrix__cell-label">
            <template v-if="isYearly">
              {{ $t("portfolio_instructions.total_mwh_label") }}
            </template>
            <template v-else>
              {{ cell.label }}
              <span
                v-if="cell.isLeapFeb"
                class="period-limit-matrix__badge is-info sm"
              >
                29
              </span>
              <span
                v-if="cell.partialRangeLabel"
                class="period-limit-matrix__cell-range"
              >
                {{ cell.partialRangeLabel }}
              </span>
            </template>
          </label>
          <input
            type="number"
            :class="[
              isYearly ? 'form-control' : 'form-control form-control-sm',
              {
                'is-invalid': yearIssueCount(group) > 0 && isCellInvalid(cell),
              },
            ]"
            min="0"
            step="1"
            :value="cell.mwh"
            @input="updateCell(group.year, cell.startDate, $event)"
          />
        </div>
      </div>

      <div class="period-limit-matrix__footer">
        <i18n-t
          keypath="portfolio_instructions.matrix_year_total"
          tag="div"
          class="period-limit-matrix__total"
        >
          <template #total>
            <strong>{{ yearTotal(group) }} MWh</strong>
          </template>
        </i18n-t>
      </div>
    </div>

    <div class="small text-muted">
      {{
        $t("portfolio_instructions.matrix_grand_total", {
          total: grandTotal,
          count: totalCellCount,
        })
      }}
    </div>

    <!-- Validation messages surface here whenever vuelidate reports any
         limit-level error — collection-level failures like
         `homogeneousDurations` or `startEndRequired` don't map to any one
         cell, so gating this on `yearIssueCount` would hide them entirely
         even though save is blocked. -->
    <div
      v-if="errors && errors.length > 0"
      class="period-limit-matrix__error"
      role="alert"
    >
      <FontAwesomeIcon icon="exclamation-triangle" />
      <span>{{ errors[0] }}</span>
    </div>
  </div>
</template>

<script setup lang="ts">
import {
  applyYearValuesToAllYears,
  flattenYearGroups,
  groupLimitsByYear,
  parseIsoDate,
  type YearGroup,
  type YearGroupCell,
} from "@/dashboard/production_strategy/overview/PPA/ppaPortfolioUtils";
import type { LimitGranularity, PeriodLimit } from "@/types";
import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome";
import { sumBy } from "lodash-es";
import { computed } from "vue";

const props = defineProps<{
  granularity: LimitGranularity;
  contractStart: string;
  contractEnd: string;
  errors?: string[];
}>();

const model = defineModel<PeriodLimit[]>({ required: true });

const isYearly = computed(() => props.granularity === "yearly");

const yearGroups = computed<YearGroup[]>(() =>
  groupLimitsByYear(
    model.value,
    props.granularity,
    parseIsoDate(props.contractStart),
    parseIsoDate(props.contractEnd)
  )
);

const yearTotal = (group: YearGroup) =>
  group.cells.reduce((sum, c) => sum + (c.mwh || 0), 0);

const grandTotal = computed(() =>
  yearGroups.value.reduce((sum, g) => sum + yearTotal(g), 0)
);

const totalCellCount = computed(() =>
  sumBy(yearGroups.value, (g) => g.cells.length)
);

const hasFullCells = (group: YearGroup) =>
  group.cells.some((c) => !c.isPartial);

const hasPartialCells = (group: YearGroup) =>
  group.cells.some((c) => c.isPartial);

// For yearly granularity the cell label is the generic "Total MWh" and the
// cell-level partial range has nowhere to render — surface it in the year
// header instead so the user can see which days the partial year covers.
const yearPartialRangeLabel = (group: YearGroup): string | undefined => {
  if (!isYearly.value) return undefined;
  const cell = group.cells[0];
  return cell?.partialRangeLabel;
};

// A cell is invalid in the "fixed amounts must be > 0" sense (the only
// per-cell validation the matrix visualizes today). Partial cells are
// included: the parent-level `fixedAmountsPositive` validator rejects
// any zero-mwh limit regardless of whether the cell covers a full or
// partial calendar period, so this must mirror that rule or save fails
// silently with no per-cell hint.
const isCellInvalid = (cell: YearGroupCell) => (cell.mwh ?? 0) <= 0;

const yearIssueCount = (group: YearGroup) => {
  if (!props.errors || props.errors.length === 0) return 0;
  return group.cells.reduce(
    (sum, cell) => sum + (isCellInvalid(cell) ? 1 : 0),
    0
  );
};

/** Emit an updated flat PeriodLimit[] after mapping the year groups. */
const emitMapped = (mapYear: (group: YearGroup) => YearGroup) => {
  model.value = flattenYearGroups(yearGroups.value.map(mapYear));
};

/** Map the cells of a single target year, leaving the rest untouched. */
const mapYearCells =
  (targetYear: number, mapCell: (cell: YearGroupCell) => YearGroupCell) =>
  (group: YearGroup): YearGroup =>
    group.year === targetYear
      ? { ...group, cells: group.cells.map(mapCell) }
      : group;

const parseMwh = (raw: string): number => {
  const value = Number.parseInt(raw || "0", 10);
  return Number.isNaN(value) ? 0 : Math.max(0, value);
};

const updateCell = (year: number, startDate: string, event: Event) => {
  const next = parseMwh((event.target as HTMLInputElement).value);
  emitMapped(
    mapYearCells(year, (cell) =>
      cell.startDate === startDate ? { ...cell, mwh: next } : cell
    )
  );
};

const applyYearToAllYears = (year: number) => {
  model.value = flattenYearGroups(
    applyYearValuesToAllYears(yearGroups.value, year)
  );
};

const resetYear = (year: number) => {
  emitMapped(mapYearCells(year, (cell) => ({ ...cell, mwh: 0 })));
};
</script>

<style scoped lang="scss">
.period-limit-matrix {
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.period-limit-matrix__year {
  padding: 16px 18px;
  border: 1px solid var(--phoenix-border-color-translucent);
  border-radius: 12px;
  background: #ffffff;

  &.is-invalid {
    // Design uses a whisper-faint red tint on white, not a saturated pink
    // fill — roughly 1.5% opacity red layered on the surface.
    border-color: rgba(var(--phoenix-danger-rgb), 0.35);
    background:
      linear-gradient(
        0deg,
        rgba(var(--phoenix-danger-rgb), 0.015),
        rgba(var(--phoenix-danger-rgb), 0.015)
      ),
      #ffffff;
  }
}

.period-limit-matrix__header {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 14px;
}

.period-limit-matrix__year-title {
  margin: 0;
  font-size: 15px;
  font-weight: 800;
  color: var(--phoenix-gray-1000);
  letter-spacing: -0.005em;
}

.period-limit-matrix__badge {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 2px 8px;
  border-radius: 999px;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;

  &.is-info {
    background: var(--phoenix-info-bg-subtle);
    color: var(--phoenix-info-text-emphasis);
  }

  &.is-warning {
    background: var(--phoenix-warning-bg-subtle);
    color: var(--phoenix-warning-text-emphasis);
  }

  &.is-danger {
    background: var(--phoenix-danger-bg-subtle);
    color: var(--phoenix-danger);
  }

  &.sm {
    padding: 1px 6px;
    font-size: 9px;
    letter-spacing: 0.04em;
  }
}

.period-limit-matrix__actions {
  margin-left: auto;
  display: flex;
  align-items: center;
  gap: 6px;
}

.period-limit-matrix__apply {
  padding: 6px 8px;
  font-size: 13px;
  font-weight: 600;
  color: var(--phoenix-primary);
  background: transparent;
  border: 0;
  border-radius: 6px;
  transition: background 0.12s ease;

  &:hover:not(:disabled) {
    background: var(--phoenix-primary-bg-subtle);
    color: var(--phoenix-primary);
  }

  &:disabled {
    opacity: 0.45;
    cursor: not-allowed;
  }
}

.period-limit-matrix__reset {
  padding: 6px 14px;
  font-size: 13px;
  font-weight: 600;
  color: var(--phoenix-gray-700);
  background: transparent;
  border: 1px solid var(--phoenix-border-color);
  border-radius: 8px;
  transition: all 0.12s ease;

  &:hover {
    background: var(--phoenix-gray-100);
    color: var(--phoenix-gray-900);
  }
}

.period-limit-matrix__grid {
  display: grid;
  grid-template-columns: repeat(6, minmax(0, 1fr));
  gap: 10px 14px;

  &.is-yearly {
    grid-template-columns: 1fr;
  }
}

.period-limit-matrix__cell {
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.period-limit-matrix__cell-label {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  font-weight: 600;
  color: var(--phoenix-gray-700);
  margin-bottom: 4px;
}

.period-limit-matrix__cell-range {
  font-size: 10px;
  font-weight: 500;
  color: var(--phoenix-gray-500);
  letter-spacing: 0;
  text-transform: none;
}

.period-limit-matrix__partial-range {
  font-size: 12px;
  font-weight: 500;
  color: var(--phoenix-gray-600);
}

.period-limit-matrix__cell input.form-control {
  font-size: 14px;
  color: var(--phoenix-gray-900);
}

.period-limit-matrix__cell input.form-control.is-invalid {
  border-color: var(--phoenix-danger);
  background-color: rgba(var(--phoenix-danger-rgb), 0.02);
  background-image: none;
  padding-right: 12px;
}

.period-limit-matrix__footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-top: 12px;
  flex-wrap: wrap;
}

.period-limit-matrix__total {
  font-size: 12px;
  color: var(--phoenix-gray-500);

  :deep(strong) {
    color: var(--phoenix-gray-1000);
    font-weight: 700;
  }
}

.period-limit-matrix__error {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  margin-top: 8px;
  font-size: 13px;
  color: var(--phoenix-danger);
  font-weight: 500;
}

@media (max-width: 768px) {
  .period-limit-matrix__grid {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }
}

@media (max-width: 480px) {
  .period-limit-matrix__grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}
</style>
